import React from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { 
  Users, Activity, FileText, DollarSign, Settings, 
  Sun, Moon, LogOut, Bell, Wifi, WifiOff,
  LayoutGrid, TrendingUp, Menu, ClipboardList, Megaphone,
  BarChart3, User, CreditCard, Download, Calendar, Crown, Package,
  Check, AlertCircle, Info, CheckCircle, XCircle, Clock, Keyboard
} from 'lucide-react';
import { useThemeStore } from '../../store/themeStore';
import { useAuthStore } from '../../store/authStore';
import { useSocketStore } from '../../store/socketStore';
import { authApi } from '../../lib/api';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { cn } from '../../lib/utils';
import { MySubscriptionDialog } from '../pages/MySubscriptionDialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ScrollArea } from '../ui/scroll-area';

// Keyboard shortcuts definition
const keyboardShortcuts = [
  { key: 'F1', action: 'Master → Clients', category: 'Master' },
  { key: 'F2', action: 'Master → Matches', category: 'Master' },
  { key: 'F3', action: 'Master → Sessions', category: 'Master' },
  { key: 'F4', action: 'Transaction → Match', category: 'Transaction' },
  { key: 'F5', action: 'Transaction → Session', category: 'Transaction' },
  { key: 'F6', action: 'Compact View', category: 'Reports' },
  { key: 'F8', action: 'Trial Balance', category: 'Reports' },
  { key: 'F9', action: 'Monday Settling', category: 'Settlement' },
  { key: 'F10', action: 'MCX & F&O', category: 'Meta' },
  { key: 'F12', action: 'Settlement', category: 'Settlement' },
];

// SuperAdmin menu items - only for platform administration
const superAdminMenuItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutGrid },
  { path: '/admin', label: 'Admin Panel', icon: Crown },
  { path: '/payments', label: 'Payments', icon: CreditCard },
  { path: '/profile', label: 'Profile', icon: User },
];

// Tenant menu items - full application features for subscribers
const tenantMenuItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutGrid },
  { path: '/master', label: 'Master', icon: Users, shortcut: 'F1', subItems: ['Clients'] },
  { path: '/transactions', label: 'Transaction', icon: Activity, shortcut: 'F4', subItems: ['Match', 'Session'] },
  { path: '/quick-entry', label: 'Quick Entry', icon: TrendingUp },
  { path: '/compact', label: 'Compact', icon: FileText, shortcut: 'F6' },
  { path: '/trial-balance', label: 'Trial Balance', icon: TrendingUp, shortcut: 'F8' },
  { path: '/settlement', label: 'Settlement', icon: DollarSign, shortcut: 'F12' },
  { path: '/monday-settling', label: 'Monday Settling', icon: Calendar, shortcut: 'F9' },
  { path: '/meta', label: 'MCX & F&O', icon: TrendingUp, shortcut: 'F10' },
];

// More menu items for tenants - reports and additional features
const tenantMoreMenuItems = [
  { path: '/net-result', label: 'Net Result', icon: TrendingUp },
  { path: '/growth', label: 'Growth', icon: BarChart3 },
  { path: '/payments', label: 'Payments', icon: CreditCard },
  { path: '/export', label: 'Export', icon: Download },
  { path: '/reconciliation', label: 'Reconciliation', icon: FileText },
  { path: '/modern-settlement', label: 'Modern Settlement', icon: DollarSign },
];

// More menu items for superadmin
const superAdminMoreMenuItems = [
  { path: '/audit-logs', label: 'Audit Logs', icon: ClipboardList },
  { path: '/profile', label: 'Profile', icon: User },
];

export function DesktopShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useThemeStore();
  const { user, logout } = useAuthStore();
  const { isConnected } = useSocketStore();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [tenantInfo, setTenantInfo] = React.useState(null);
  const [notifications, setNotifications] = React.useState([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [notifOpen, setNotifOpen] = React.useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifications/my?limit=20`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifications/my/unread-count`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/notifications/my/${notificationId}/read`, {
        method: 'PUT',
        credentials: 'include'
      });
      fetchUnreadCount();
      fetchNotifications();
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${API_URL}/api/notifications/my/mark-all-read`, {
        method: 'PUT',
        credentials: 'include'
      });
      setUnreadCount(0);
      fetchNotifications();
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  // Fetch tenant info on mount
  React.useEffect(() => {
    const fetchTenantInfo = async () => {
      try {
        const info = await authApi.tenantInfo();
        setTenantInfo(info);
      } catch (err) {
        console.error('Failed to fetch tenant info:', err);
      }
    };
    
    if (user?.tenant_id) {
      fetchTenantInfo();
      fetchNotifications();
      fetchUnreadCount();
      
      // Poll for new notifications every 60 seconds
      const interval = setInterval(() => {
        fetchUnreadCount();
      }, 60000);
      
      return () => clearInterval(interval);
    }
  }, [user?.tenant_id]);

  // Determine menu items based on user role
  const isSuperAdmin = user?.role === 'superadmin';
  const menuItems = isSuperAdmin ? superAdminMenuItems : tenantMenuItems;
  const moreMenuItems = isSuperAdmin ? superAdminMoreMenuItems : tenantMoreMenuItems;
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = React.useState(false);
  const [shortcutsDialogOpen, setShortcutsDialogOpen] = React.useState(false);

  // Handle keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts if user is typing in an input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }

      // Keyboard shortcuts mapping
      const shortcuts = {
        'F1': { path: '/master', tab: 'clients' },      // Master > Client
        'F2': { path: '/master', tab: 'matches' },      // Master > Matches
        'F3': { path: '/master', tab: 'sessions' },     // Master > Session
        'F4': { path: '/transactions', tab: 'match' },  // Transaction > Match
        'F5': { path: '/transactions', tab: 'session' }, // Transaction > Session
        'F6': { path: '/compact' },                     // Compact
        'F7': { action: 'help' },                       // Show keyboard shortcuts help
        'F8': { path: '/trial-balance' },               // Trial Balance
        'F9': { path: '/monday-settling' },             // Monday Settling
        'F10': { path: '/meta' },                       // MCX & F&O
        'F12': { path: '/settlement' },                 // Settlement
      };

      if (shortcuts[e.key]) {
        e.preventDefault();
        const shortcut = shortcuts[e.key];
        
        if (shortcut.action === 'help') {
          setShortcutsDialogOpen(true);
        } else if (shortcut.path) {
          // For F4/F5 on transactions page, let the page handle it internally
          // This preserves match/session selection when switching tabs
          const currentPath = location.pathname;
          if ((e.key === 'F4' || e.key === 'F5') && currentPath === '/transactions') {
            // Don't navigate - the TransactionsPage handles F4/F5 internally
            return;
          }
          
          // Navigate with tab parameter if specified
          if (shortcut.tab) {
            navigate(`${shortcut.path}?tab=${shortcut.tab}`);
          } else {
            navigate(shortcut.path);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="desktop-shell" data-testid="desktop-shell">
      {/* Header */}
      <header className="desktop-header" data-testid="desktop-header">
        <div className="flex items-center gap-4">
          {/* Mobile menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <nav className="flex flex-col py-4">
                <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase">Main</div>
                {menuItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setMobileMenuOpen(false);
                    }}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 text-sm hover:bg-muted transition-colors",
                      location.pathname === item.path && "bg-primary/10 text-primary"
                    )}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.label}</span>
                    {item.shortcut && <span className="kbd ml-auto">{item.shortcut}</span>}
                  </button>
                ))}
                <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase mt-2">More</div>
                {moreMenuItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setMobileMenuOpen(false);
                    }}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 text-sm hover:bg-muted transition-colors",
                      location.pathname === item.path && "bg-primary/10 text-primary"
                    )}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </button>
                ))}
              </nav>
            </SheetContent>
          </Sheet>

          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-sm bg-primary flex items-center justify-center">
              <span className="font-heading font-bold text-primary-foreground text-sm">YG</span>
            </div>
            <span className="font-heading font-bold text-lg hidden sm:inline">YellowGold Pro</span>
          </div>

          {/* Connection status */}
          <div className="flex items-center gap-1.5">
            {isConnected ? (
              <Wifi className="h-3.5 w-3.5 text-emerald-500" />
            ) : (
              <WifiOff className="h-3.5 w-3.5 text-destructive" />
            )}
            <span className="text-xs text-muted-foreground hidden sm:inline">
              {isConnected ? 'Live' : 'Offline'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Keyboard shortcuts help */}
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setShortcutsDialogOpen(true)}
            title="Keyboard Shortcuts (F7)"
            data-testid="keyboard-shortcuts-btn"
          >
            <Keyboard className="h-4 w-4" />
          </Button>

          {/* Theme toggle */}
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={toggleTheme}
            data-testid="theme-toggle"
          >
            {theme === 'dark' ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </Button>

          {/* Notifications */}
          <Popover open={notifOpen} onOpenChange={(open) => {
            setNotifOpen(open);
            if (open) fetchNotifications();
          }}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                <Bell className="h-4 w-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h4 className="font-semibold text-sm">Notifications</h4>
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" className="text-xs h-7" onClick={markAllAsRead}>
                    Mark all read
                  </Button>
                )}
              </div>
              <ScrollArea className="h-80">
                {notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                    <Bell className="h-8 w-8 mb-2 opacity-50" />
                    <p className="text-sm">No notifications</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {notifications.map((notif) => (
                      <div 
                        key={notif.notification_id}
                        className={cn(
                          "px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors",
                          !notif.is_read && "bg-primary/5"
                        )}
                        onClick={() => {
                          if (!notif.is_read) markAsRead(notif.notification_id);
                          if (notif.link) {
                            navigate(notif.link);
                            setNotifOpen(false);
                          }
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <div className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                            notif.type === 'success' && "bg-green-500/20 text-green-500",
                            notif.type === 'warning' && "bg-yellow-500/20 text-yellow-500",
                            notif.type === 'error' && "bg-red-500/20 text-red-500",
                            notif.type === 'info' && "bg-blue-500/20 text-blue-500"
                          )}>
                            {notif.type === 'success' && <CheckCircle className="h-4 w-4" />}
                            {notif.type === 'warning' && <AlertCircle className="h-4 w-4" />}
                            {notif.type === 'error' && <XCircle className="h-4 w-4" />}
                            {notif.type === 'info' && <Info className="h-4 w-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={cn(
                              "text-sm",
                              !notif.is_read && "font-medium"
                            )}>{notif.title}</p>
                            <p className="text-xs text-muted-foreground truncate">{notif.message}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(notif.created_at).toLocaleString()}
                            </p>
                          </div>
                          {!notif.is_read && (
                            <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-2" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </PopoverContent>
          </Popover>

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                {user?.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user?.name} 
                    className="w-6 h-6 rounded-full"
                  />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-medium">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                )}
                <span className="text-sm hidden sm:inline">{user?.name?.split(' ')[0]}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-menu-item">
                <User className="h-4 w-4 mr-2" />
                Profile
              </DropdownMenuItem>
              {!isSuperAdmin && (
                <DropdownMenuItem onClick={() => setSubscriptionDialogOpen(true)} data-testid="subscription-menu-item">
                  <Crown className="h-4 w-4 mr-2" />
                  My Subscription
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Menu Strip */}
      <div className="menu-strip hidden md:flex" data-testid="menu-strip">
        {menuItems.map((item) => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className={cn(
              "menu-item flex items-center gap-1.5",
              location.pathname === item.path && "active"
            )}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <item.icon className="h-3.5 w-3.5" />
            <span>{item.label}</span>
            {item.shortcut && <span className="kbd ml-1">{item.shortcut}</span>}
          </button>
        ))}
        
        {/* More dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="menu-item flex items-center gap-1.5" data-testid="nav-more">
              <Menu className="h-3.5 w-3.5" />
              <span>More</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {moreMenuItems.map((item) => (
              <DropdownMenuItem 
                key={item.path} 
                onClick={() => navigate(item.path)}
                data-testid={`nav-more-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="h-4 w-4 mr-2" />
                {item.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Workspace */}
      <div className="desktop-workspace">
        <main className="desktop-main" data-testid="main-content">
          <Outlet />
        </main>
      </div>

      {/* Footer */}
      <footer className="desktop-footer" data-testid="desktop-footer">
        <div className="flex items-center gap-4">
          <span>Tenant: {tenantInfo?.tenant_email || user?.email || 'Loading...'}</span>
          <span>
            {isSuperAdmin ? 'Role: SuperAdmin' : `User ${tenantInfo?.user_position || 1}/${tenantInfo?.max_users || 1}`}
          </span>
          {!isSuperAdmin && tenantInfo?.plan && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-primary/20 text-primary">
              {tenantInfo.plan.charAt(0).toUpperCase() + tenantInfo.plan.slice(1)} Plan
            </span>
          )}
          {/* Trial Days Indicator */}
          {!isSuperAdmin && tenantInfo?.is_trial && tenantInfo?.trial_days_remaining !== null && (
            <span 
              className={cn(
                "text-xs px-2 py-0.5 rounded flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity",
                tenantInfo.trial_days_remaining <= 7 
                  ? "bg-amber-500/20 text-amber-500" 
                  : "bg-blue-500/20 text-blue-500"
              )}
              onClick={() => navigate('/select-plan')}
              data-testid="trial-days-indicator"
            >
              <Clock className="h-3 w-3" />
              {tenantInfo.trial_days_remaining <= 0 
                ? "Trial Expired" 
                : `${tenantInfo.trial_days_remaining} days left`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <span>© 2025 YellowGold Pro</span>
          <span className={cn(
            "flex items-center gap-1",
            isConnected ? "text-emerald-500" : "text-destructive"
          )}>
            <span className={cn(
              "w-2 h-2 rounded-full",
              isConnected ? "bg-emerald-500" : "bg-destructive"
            )} />
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
        </div>
      </footer>

      {/* Subscription Dialog */}
      {!isSuperAdmin && (
        <MySubscriptionDialog 
          open={subscriptionDialogOpen} 
          onOpenChange={setSubscriptionDialogOpen} 
        />
      )}

      {/* Keyboard Shortcuts Dialog */}
      <Dialog open={shortcutsDialogOpen} onOpenChange={setShortcutsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Keyboard className="h-5 w-5 text-primary" />
              Keyboard Shortcuts
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Group shortcuts by category */}
            {['Master', 'Transaction', 'Reports', 'Settlement', 'Meta'].map(category => {
              const categoryShortcuts = keyboardShortcuts.filter(s => s.category === category);
              if (categoryShortcuts.length === 0) return null;
              
              return (
                <div key={category}>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">{category}</h4>
                  <div className="space-y-1">
                    {categoryShortcuts.map(shortcut => (
                      <div key={shortcut.key} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50">
                        <span className="text-sm">{shortcut.action}</span>
                        <kbd className="px-2 py-1 text-xs font-mono bg-muted rounded border">{shortcut.key}</kbd>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            
            {/* Help shortcut */}
            <div className="pt-2 border-t">
              <div className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50">
                <span className="text-sm text-muted-foreground">Show this dialog</span>
                <kbd className="px-2 py-1 text-xs font-mono bg-muted rounded border">F7</kbd>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
